/**
 * Price Alerts Module
 * 価格アラート - 指定価格への到達を監視・通知
 */

import { state, saveState } from '../state.js';
import { fetchStockData } from '../api/index.js';

// Constants
const CHECK_INTERVAL = 30000; // 30 seconds
let checkIntervalId = null;

// DOM Elements (cached on init)
let alertsPanel, alertsList, addAlertBtn;

/**
 * Initialize Alerts Module
 */
export function initAlerts() {
    alertsPanel = document.getElementById('alerts-panel');
    alertsList = document.getElementById('alerts-list');
    addAlertBtn = document.getElementById('add-alert-btn');

    if (!alertsPanel) return;

    // Initialize state if not exists
    if (!state.alerts) {
        state.alerts = [];
    }
    if (!state.alertHistory) {
        state.alertHistory = [];
    }

    // Event Listeners
    if (addAlertBtn) {
        addAlertBtn.addEventListener('click', showAddAlertModal);
    }

    // Request notification permission
    requestNotificationPermission();

    // Start background price checking
    startAlertMonitor();

    // Initial render
    renderAlerts();
}

/**
 * Request browser notification permission
 */
async function requestNotificationPermission() {
    if ('Notification' in window) {
        if (Notification.permission === 'default') {
            await Notification.requestPermission();
        }
    }
}

/**
 * Create a new price alert
 */
export function createAlert(symbol, condition, targetPrice) {
    const alert = {
        id: Date.now().toString(),
        symbol: symbol.toUpperCase(),
        condition, // 'above' or 'below'
        targetPrice: parseFloat(targetPrice),
        active: true,
        createdAt: new Date().toISOString(),
        triggeredAt: null
    };

    state.alerts.push(alert);
    saveState();
    renderAlerts();

    return alert;
}

/**
 * Remove an alert
 */
export function removeAlert(alertId) {
    state.alerts = state.alerts.filter(a => a.id !== alertId);
    saveState();
    renderAlerts();
}

/**
 * Toggle alert active state
 */
export function toggleAlert(alertId) {
    const alert = state.alerts.find(a => a.id === alertId);
    if (alert) {
        alert.active = !alert.active;
        saveState();
        renderAlerts();
    }
}

/**
 * Start background price monitoring
 */
function startAlertMonitor() {
    if (checkIntervalId) {
        clearInterval(checkIntervalId);
    }

    // Check immediately if there are active alerts
    if (hasActiveAlerts()) {
        checkAlerts();
    }

    // Set up interval
    checkIntervalId = setInterval(() => {
        if (hasActiveAlerts()) {
            checkAlerts();
        }
    }, CHECK_INTERVAL);
}

/**
 * Stop background monitoring
 */
export function stopAlertMonitor() {
    if (checkIntervalId) {
        clearInterval(checkIntervalId);
        checkIntervalId = null;
    }
}

/**
 * Check if there are any active alerts
 */
function hasActiveAlerts() {
    return state.alerts.some(a => a.active);
}

/**
 * Check all active alerts
 */
async function checkAlerts() {
    const activeAlerts = state.alerts.filter(a => a.active);

    // Group by symbol to minimize API calls
    const symbolGroups = {};
    activeAlerts.forEach(alert => {
        if (!symbolGroups[alert.symbol]) {
            symbolGroups[alert.symbol] = [];
        }
        symbolGroups[alert.symbol].push(alert);
    });

    for (const symbol of Object.keys(symbolGroups)) {
        try {
            const data = await fetchStockData(symbol, '1D');
            if (data && data.candleData && data.candleData.length > 0) {
                const latestCandle = data.candleData[data.candleData.length - 1];
                const currentPrice = latestCandle.close;

                symbolGroups[symbol].forEach(alert => {
                    if (checkAlertCondition(alert, currentPrice)) {
                        triggerAlert(alert, currentPrice);
                    }
                });
            }
        } catch (error) {
            console.warn(`Failed to check alerts for ${symbol}:`, error.message);
        }
    }
}

/**
 * Check if alert condition is met
 */
function checkAlertCondition(alert, currentPrice) {
    if (alert.condition === 'above') {
        return currentPrice >= alert.targetPrice;
    } else if (alert.condition === 'below') {
        return currentPrice <= alert.targetPrice;
    }
    return false;
}

/**
 * Trigger an alert
 */
function triggerAlert(alert, currentPrice) {
    // Deactivate the alert
    alert.active = false;
    alert.triggeredAt = new Date().toISOString();

    // Add to history
    state.alertHistory.push({
        ...alert,
        triggeredPrice: currentPrice
    });

    saveState();
    renderAlerts();

    // Show notification
    showAlertNotification(alert, currentPrice);

    // Show toast
    showToastMessage(alert, currentPrice);
}

/**
 * Show browser notification
 */
function showAlertNotification(alert, currentPrice) {
    if ('Notification' in window && Notification.permission === 'granted') {
        const conditionText = alert.condition === 'above' ? '以上' : '以下';
        const notification = new Notification(`🔔 ${alert.symbol} Price Alert`, {
            body: `価格が $${alert.targetPrice.toFixed(2)} ${conditionText}になりました。現在価格: $${currentPrice.toFixed(2)}`,
            icon: '/favicon.svg',
            tag: `alert-${alert.id}`,
            requireInteraction: true
        });

        notification.onclick = () => {
            window.focus();
            notification.close();
        };
    }
}

/**
 * Show toast notification
 */
function showToastMessage(alert, currentPrice) {
    const conditionText = alert.condition === 'above' ? '以上' : '以下';
    const message = `${alert.symbol} が $${alert.targetPrice.toFixed(2)} ${conditionText}になりました！現在: $${currentPrice.toFixed(2)}`;

    // Dispatch toast event (handled by ui-manager)
    const event = new CustomEvent('show-toast', {
        detail: { message, type: 'success' }
    });
    document.dispatchEvent(event);
}

/**
 * Render alerts UI
 */
export function renderAlerts() {
    if (!alertsList) return;

    if (!state.alerts || state.alerts.length === 0) {
        alertsList.innerHTML = '<p class="empty-state">No active alerts.</p>';
        return;
    }

    const alertsHTML = state.alerts.map(alert => {
        const conditionSymbol = alert.condition === 'above' ? '≥' : '≤';
        const statusClass = alert.active ? 'active' : 'inactive';
        const statusIcon = alert.active ? '🔔' : '🔕';

        return `
            <div class="alert-item ${statusClass}" data-id="${alert.id}">
                <div class="alert-info">
                    <span class="alert-symbol">${alert.symbol}</span>
                    <span class="alert-condition">
                        ${statusIcon} Price ${conditionSymbol} $${alert.targetPrice.toFixed(2)}
                    </span>
                </div>
                <div class="alert-actions">
                    <button class="alert-toggle-btn" data-id="${alert.id}" title="${alert.active ? 'Pause' : 'Resume'}">
                        ${alert.active ? '⏸️' : '▶️'}
                    </button>
                    <button class="alert-remove-btn" data-id="${alert.id}" title="Remove">×</button>
                </div>
            </div>
        `;
    }).join('');

    alertsList.innerHTML = alertsHTML;

    // Add event listeners
    alertsList.querySelectorAll('.alert-toggle-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            toggleAlert(btn.dataset.id);
        });
    });

    alertsList.querySelectorAll('.alert-remove-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            if (confirm('Remove this alert?')) {
                removeAlert(btn.dataset.id);
            }
        });
    });
}

/**
 * Show Add Alert Modal
 */
function showAddAlertModal() {
    let modal = document.getElementById('alert-modal');
    if (!modal) {
        modal = document.createElement('div');
        modal.id = 'alert-modal';
        modal.className = 'modal-overlay';
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3>New Price Alert</h3>
                    <button class="modal-close-btn">×</button>
                </div>
                <form id="alert-form" class="modal-form">
                    <div class="form-group">
                        <label for="alert-symbol">Symbol</label>
                        <input type="text" id="alert-symbol" placeholder="AAPL" required maxlength="10">
                    </div>
                    <div class="form-group">
                        <label for="alert-condition">Condition</label>
                        <select id="alert-condition" required>
                            <option value="above">Price goes above</option>
                            <option value="below">Price goes below</option>
                        </select>
                    </div>
                    <div class="form-group">
                        <label for="alert-price">Target Price ($)</label>
                        <input type="number" id="alert-price" placeholder="200.00" required min="0.01" step="0.01">
                    </div>
                    <div class="form-actions">
                        <button type="button" class="btn-secondary modal-cancel-btn">Cancel</button>
                        <button type="submit" class="btn-primary">Create Alert</button>
                    </div>
                </form>
            </div>
        `;
        document.body.appendChild(modal);

        // Event Listeners
        modal.querySelector('.modal-close-btn').addEventListener('click', hideAlertModal);
        modal.querySelector('.modal-cancel-btn').addEventListener('click', hideAlertModal);
        modal.addEventListener('click', (e) => {
            if (e.target === modal) hideAlertModal();
        });

        modal.querySelector('#alert-form').addEventListener('submit', (e) => {
            e.preventDefault();
            const symbol = document.getElementById('alert-symbol').value.trim();
            const condition = document.getElementById('alert-condition').value;
            const price = document.getElementById('alert-price').value;

            if (symbol && condition && price) {
                createAlert(symbol, condition, price);
                hideAlertModal();
                e.target.reset();
            }
        });
    }

    // Pre-fill with current symbol if available
    if (state.currentSymbol) {
        const symbolInput = modal.querySelector('#alert-symbol');
        if (symbolInput) {
            symbolInput.value = state.currentSymbol;
        }
    }

    modal.classList.add('active');
    document.getElementById('alert-symbol').focus();
}

function hideAlertModal() {
    const modal = document.getElementById('alert-modal');
    if (modal) modal.classList.remove('active');
}

// Handle toast events
document.addEventListener('show-toast', (e) => {
    const { message, type } = e.detail;
    const toastContainer = document.getElementById('toast-container');
    if (toastContainer) {
        const toast = document.createElement('div');
        toast.className = `toast ${type || 'info'}`;
        toast.textContent = message;
        toastContainer.appendChild(toast);

        setTimeout(() => {
            toast.classList.add('hiding');
            setTimeout(() => toast.remove(), 300);
        }, 5000);
    }
});
